import React from 'react';
import { ComposedChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { motion } from 'framer-motion';
type CandleData = {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
};
const generateCandleData = (): CandleData[] => {
  const data: CandleData[] = [];
  let price = 4250;
  for (let i = 0; i < 30; i++) {
    const volatility = Math.random() * 80 + 20;
    const direction = Math.random() > 0.45 ? 1 : -1;
    const change = direction * volatility;
    const open = price;
    const close = price + change;
    const high = Math.max(open, close) + Math.random() * 30;
    const low = Math.min(open, close) - Math.random() * 30;
    data.push({
      date: `${i + 1}`,
      open: Math.round(open),
      high: Math.round(high),
      low: Math.round(low),
      close: Math.round(close)
    });
    price = close;
  }
  return data;
};
const candleData = generateCandleData();
export function CandlestickChart({
  className = ''
}: {
  className?: string;
}) {
  // Transform data for the chart
  const chartData = candleData.map(candle => ({
    ...candle,
    // Body of the candle (open to close)
    bodyBottom: Math.min(candle.open, candle.close),
    bodyHeight: Math.abs(candle.close - candle.open),
    // Wick (high to low)
    wickBottom: candle.low,
    wickHeight: candle.high - candle.low,
    isBullish: candle.close >= candle.open
  }));
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.8
  }} className={`w-full h-full ${className}`} data-id="element-10">
      <ResponsiveContainer width="100%" height="100%" data-id="element-11">
        <ComposedChart data={chartData} margin={{
        top: 10,
        right: 10,
        left: 10,
        bottom: 10
      }} data-id="element-12">
          <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
          fill: '#6b7280',
          fontSize: 10
        }} data-id="element-13" />
          <YAxis domain={['dataMin - 50', 'dataMax + 50']} axisLine={false} tickLine={false} tick={{
          fill: '#6b7280',
          fontSize: 10
        }} orientation="right" data-id="element-14" />
          <ReferenceLine y={candleData[candleData.length - 1].close} stroke="#f59e0b" strokeDasharray="3 3" data-id="element-15" />

          {/* Wicks */}
          <Bar dataKey="wickHeight" stackId="wick" fill="transparent" barSize={2} data-id="element-16">
            {chartData.map((entry, index) => <Cell key={`wick-${index}`} fill={entry.isBullish ? '#10b981' : '#ef4444'} data-id="element-17" />)}
          </Bar>

          {/* Bodies */}
          <Bar dataKey="bodyHeight" stackId="body" barSize={8} data-id="element-18">
            {chartData.map((entry, index) => <Cell key={`body-${index}`} fill={entry.isBullish ? '#10b981' : '#ef4444'} data-id="element-19" />)}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
    </motion.div>;
}
// Simplified visual candlestick for hero background
export function CandlestickBackground() {
  return <div className="absolute inset-0 overflow-hidden opacity-20" data-id="element-20">
      <svg className="w-full h-full" viewBox="0 0 1200 600" preserveAspectRatio="xMidYMid slice" data-id="element-21">
        {candleData.map((candle, i) => {
        const x = 40 + i * 38;
        const isBullish = candle.close >= candle.open;
        const color = isBullish ? '#10b981' : '#ef4444';
        const bodyTop = Math.max(candle.open, candle.close);
        const bodyBottom = Math.min(candle.open, candle.close);
        const scale = 0.08;
        return <g key={i} data-id="element-22">
              {/* Wick */}
              <motion.line x1={x} y1={600 - candle.high * scale} x2={x} y2={600 - candle.low * scale} stroke={color} strokeWidth="1" initial={{
            pathLength: 0
          }} animate={{
            pathLength: 1
          }} transition={{
            duration: 0.5,
            delay: i * 0.03
          }} data-id="element-23" />
              {/* Body */}
              <motion.rect x={x - 6} y={600 - bodyTop * scale} width="12" height={Math.max((bodyTop - bodyBottom) * scale, 2)} fill={color} initial={{
            scaleY: 0
          }} animate={{
            scaleY: 1
          }} transition={{
            duration: 0.3,
            delay: i * 0.03 + 0.2
          }} data-id="element-24" />
            </g>;
      })}
      </svg>
    </div>;
}